import React from "react";
import { useForm } from "react-hook-form";
import { X, UserIcon, FileTextIcon } from "lucide-react";
import { Permission } from "../../api/services/permissions/type";
import { CreateRoleRequest } from "../../api/services/roles/type";
import CustomInput from "../ui/CustomInput";
import PrimaryButton from "../ui/PrimaryButton";
import { useAllMenus } from "../../hooks/useAllMenus";

interface RoleFormProps {
  permissions: Permission[];
  isLoading?: boolean;
  onSubmit: (data: CreateRoleRequest) => void;
  onCancel: () => void;
}

export const RoleForm: React.FC<RoleFormProps> = ({
  permissions,
  isLoading = false,
  onSubmit,
  onCancel,
}) => {
  const { data: availableMenus = [], isLoading: menusLoading } = useAllMenus();
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<CreateRoleRequest>({
    defaultValues: {
      name: "",
      description: "",
      is_system_role: false,
      permissionIds: [],
      sidebarMenuIds: [],
    },
  });

  const selectedPermissions = watch("permissionIds") || [];
  const selectedMenus = watch("sidebarMenuIds") || [];

  const groupedPermissions = React.useMemo(() => {
    if (!permissions || permissions.length === 0) {
      return {};
    }
    return permissions.reduce(
      (acc, permission) => {
        if (!acc[permission.tableName]) {
          acc[permission.tableName] = [];
        }
        acc[permission.tableName].push(permission);
        return acc;
      },
      {} as Record<string, Permission[]>,
    );
  }, [permissions]);

  const handlePermissionChange = (permissionId: string, checked: boolean) => {
    if (checked) {
      setValue("permissionIds", [...selectedPermissions, permissionId]);
    } else {
      setValue(
        "permissionIds",
        selectedPermissions.filter((id) => id !== permissionId),
      );
    }
  };

  const handleSelectAll = () => {
    if (selectedPermissions.length === permissions.length) {
      setValue("permissionIds", []);
    } else {
      setValue(
        "permissionIds",
        permissions.map((p) => p.id),
      );
    }
  };

  const handleSelectAllInGroup = (groupPermissions: Permission[]) => {
    const groupIds = groupPermissions.map((p) => p.id);
    const isAllGroupSelected = groupIds.every((id) =>
      selectedPermissions.includes(id),
    );

    if (isAllGroupSelected) {
      setValue(
        "permissionIds",
        selectedPermissions.filter((id) => !groupIds.includes(id)),
      );
    } else {
      const newSelected = [...selectedPermissions];
      groupIds.forEach((id) => {
        if (!newSelected.includes(id)) {
          newSelected.push(id);
        }
      });
      setValue("permissionIds", newSelected);
    }
  };

  const handleMenuChange = (menuId: string, checked: boolean) => {
    if (checked) {
      setValue("sidebarMenuIds", [...selectedMenus, menuId]);
    } else {
      setValue(
        "sidebarMenuIds",
        selectedMenus.filter((id) => id !== menuId),
      );
    }
  };

  const handleSelectAllMenus = () => {
    if (selectedMenus.length === availableMenus.length) {
      setValue("sidebarMenuIds", []);
    } else {
      setValue(
        "sidebarMenuIds",
        availableMenus.map((m) => m.id),
      );
    }
  };

  const isAllSelected = selectedPermissions.length === permissions.length;
  const isSomeSelected =
    selectedPermissions.length > 0 &&
    selectedPermissions.length < permissions.length;

  const onFormSubmit = (data: CreateRoleRequest) => {
    onSubmit(data);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-lg w-full max-w-6xl h-[90vh] flex flex-col">
        <div className="bg-slate-800 p-4 border-b border-slate-700 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-semibold text-white">
            Add Roles and Permission
          </h2>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-white transition-colors"
            disabled={isLoading}
          >
            <X size={20} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit(onFormSubmit)}
          className="flex-1 overflow-hidden flex flex-col"
        >
          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto">
            <div className="px-6 pt-4 pb-2">
              <div className="text-slate-300 text-sm mb-3">
                Please fill in the basic details to get started.
              </div>

              {/* Basic Role Information */}
              <div className="grid grid-cols-2 gap-4">
                <CustomInput
                  label="Title of Role"
                  type="text"
                  placeholder="Role"
                  register={register}
                  name="name"
                  validation={{
                    required: "Role name is required",
                    validate: (v: unknown) => {
                      const trimmed = String(v ?? "").trim();
                      if (trimmed.length === 0)
                        return "Role name cannot be empty";
                      if (trimmed.length < 2)
                        return "Role name must be at least 2 characters";
                      return true;
                    },
                  }}
                  error={errors.name}
                  disabled={isLoading}
                  icon={<UserIcon className="w-4 h-4 text-slate-400" />}
                />

                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">
                    Description
                  </label>
                  <div className="relative">
                    <FileTextIcon className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <input
                      {...register("description", {})}
                      type="text"
                      placeholder="Role description"
                      disabled={isLoading}
                      className="w-full pl-10 pr-3 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-400">
                      {errors.description.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Permissions Selection */}
              <div className="px-6 pb-4">
                <div className="flex items-center justify-between py-3 border-b border-slate-700">
                  <div>
                    <h3 className="text-base font-medium text-white">
                      Select Permissions
                    </h3>
                    <div className="text-xs text-slate-400">
                      {selectedPermissions.length} of {permissions.length}{" "}
                      selected
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className={`px-3 py-1.5 rounded-lg transition-colors font-medium text-xs ${
                      isAllSelected
                        ? "bg-blue-600 text-white hover:bg-blue-700"
                        : isSomeSelected
                          ? "bg-blue-600/50 text-white hover:bg-blue-600"
                          : "bg-slate-700 text-slate-300 hover:bg-slate-600 border border-slate-600"
                    }`}
                    disabled={isLoading}
                  >
                    {isAllSelected ? "Deselect All" : "Select All"}
                  </button>
                </div>

                <div className="max-h-64 overflow-y-auto py-3 border border-slate-700 rounded-lg mt-2">
                  {permissions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                      <p className="text-lg mb-2">No permissions available</p>
                      <p className="text-sm">
                        Please check if permissions are loaded correctly.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4 p-2">
                      {Object.entries(groupedPermissions).map(
                        ([tableName, tablePermissions]) => (
                          <div
                            key={tableName}
                            className="bg-slate-750 rounded-lg p-3"
                          >
                            {/* Table Name Header */}
                            <div className="flex items-center justify-between mb-2">
                              <div className="text-slate-300 font-medium text-sm capitalize">
                                {tableName.replace(/([A-Z])/g, " $1").trim()}
                              </div>
                              <button
                                type="button"
                                onClick={() =>
                                  handleSelectAllInGroup(tablePermissions)
                                }
                                className={`px-2 py-0.5 rounded text-xs transition-colors ${
                                  tablePermissions.every((p) =>
                                    selectedPermissions.includes(p.id),
                                  )
                                    ? "bg-green-600 text-white hover:bg-green-700"
                                    : tablePermissions.some((p) =>
                                          selectedPermissions.includes(p.id),
                                        )
                                      ? "bg-green-600/50 text-white hover:bg-green-600"
                                      : "bg-slate-600 text-slate-300 hover:bg-slate-500"
                                }`}
                                disabled={isLoading}
                              >
                                {tablePermissions.every((p) =>
                                  selectedPermissions.includes(p.id),
                                )
                                  ? "All"
                                  : "Select"}
                              </button>
                            </div>

                            {/* Permissions for this table */}
                            <div className="space-y-1">
                              {tablePermissions.map((permission) => (
                                <label
                                  key={permission.id}
                                  className="flex items-start space-x-2 cursor-pointer hover:bg-slate-700 p-1.5 rounded text-xs"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedPermissions.includes(
                                      permission.id,
                                    )}
                                    onChange={(e) =>
                                      handlePermissionChange(
                                        permission.id,
                                        e.target.checked,
                                      )
                                    }
                                    disabled={isLoading}
                                    className="w-3.5 h-3.5 mt-0.5 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500 focus:ring-1"
                                  />
                                  <div className="flex-1">
                                    <div className="text-slate-200 text-xs leading-tight">
                                      {permission.description}
                                    </div>
                                    <div className="text-[10px] text-slate-500">
                                      {permission.action}
                                    </div>
                                  </div>
                                </label>
                              ))}
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Sidebar Menu Selection */}
              <div className="px-6 pb-4">
                <div className="flex items-center justify-between py-3 border-b border-slate-700">
                  <div>
                    <h3 className="text-base font-medium text-white">
                      Select Sidebar Menus
                    </h3>
                    <div className="text-xs text-slate-400">
                      {selectedMenus.length} of {availableMenus.length} selected
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleSelectAllMenus}
                    className={`px-3 py-1.5 rounded-lg transition-colors font-medium text-xs ${
                      selectedMenus.length === availableMenus.length
                        ? "bg-green-600 text-white hover:bg-green-700"
                        : selectedMenus.length > 0
                          ? "bg-green-600/50 text-white hover:bg-green-600"
                          : "bg-slate-700 text-slate-300 hover:bg-slate-600 border border-slate-600"
                    }`}
                    disabled={isLoading || menusLoading}
                  >
                    {selectedMenus.length === availableMenus.length
                      ? "Deselect All"
                      : "Select All"}
                  </button>
                </div>

                <div className="max-h-48 overflow-y-auto py-3 border border-slate-700 rounded-lg mt-2">
                  {menusLoading ? (
                    <div className="text-center py-4 text-slate-400">
                      Loading sidebar menus...
                    </div>
                  ) : availableMenus.length === 0 ? (
                    <div className="text-center py-4 text-slate-400">
                      No sidebar menus available
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 p-2">
                      {availableMenus.map((menu) => (
                        <label
                          key={menu.id}
                          className="flex items-start space-x-3 cursor-pointer hover:bg-slate-700 p-2 rounded text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={selectedMenus.includes(menu.id)}
                            onChange={(e) =>
                              handleMenuChange(menu.id, e.target.checked)
                            }
                            disabled={isLoading}
                            className="w-4 h-4 mt-0.5 text-green-600 bg-slate-700 border-slate-600 rounded focus:ring-green-500 focus:ring-1"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-slate-200 font-medium">
                              {menu.menu_name}
                            </div>
                            <div className="text-xs text-slate-500 truncate">
                              {menu.route_path}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 p-4 border-t border-slate-700 flex-shrink-0">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              Cancel
            </button>
            <PrimaryButton
              type="submit"
              isLoading={isLoading}
              disabled={isLoading}
              className="flex-1 py-2 text-sm"
            >
              Add
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
};
